import { Component } from '@theme/component';
import { FilterUpdateManager, LoadManager, StepInfoManager } from '@theme/custom-collection-utility';
import { DeliveryEvents } from '@theme/delivery-core';

function isEcommCollectionRecommendationsEnabled() {
  return /** @type {any} */ (window).themeSettings?.enableEcommCollectionRecommendations !== false;
}

/**
 * A custom element that manages the collection footer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} footerContainer - The footer container element.
 * @property {HTMLElement} minorderProgressWrapper - The min order progress wrapper element.
 * @property {HTMLElement} shippingProgressWrapper - The shipping progress wrapper element.
 * @property {HTMLElement} customProgressBarWrapper - The custom progress bar wrapper element.
 * @property {HTMLElement} collectionCartButton - The collection cart button element.
 * @property {HTMLElement} nextButton - The next button element.
 *
 * @extends {Component<Refs>}
 */
export class CustomCollectionFooterComponent extends Component {
  requiredRefs = ['footerContainer'];

  connectedCallback() {
    try {
      super.connectedCallback();

      this.minOrderValue = parseFloat(this.getAttribute('data-minimum-order-value') || '0');
      this.freeShippingThreshold = parseFloat(this.getAttribute('data-free-shipping-threshold') || '0');
      this.cartTotal = parseFloat(this.getAttribute('data-cart-total') || '0');
      this.cartItemCount = parseFloat(this.getAttribute('data-cart-item-count') || '0');

      /** @type {any} */
      this.stepInfo = StepInfoManager.getStepInfo();

      /** @type {number} */
      this.steps = this.stepInfo ? (this.stepInfo['step-count'] || 1) : 1;
      
      /** @type {number} */
      this.stepsCount = Number(this.stepInfo ? this.stepInfo['total-steps'] : this.refs.footerContainer?.getAttribute('data-total-steps') ? this.refs.footerContainer?.getAttribute('data-total-steps') : 1);
      
      document.addEventListener('flow-visual-update-requested', this.#handleFlowVisualStateUpdates);
      document.addEventListener(DeliveryEvents.POSTAL_CODE_CHANGED, this.#handlePostalCodeChanged);

      const closeMinOrderInfoBtn = this.querySelector('.collection-close-min-order-info-button');
      const closeShippingInfoBtn = this.querySelector('.collection-close-shipping-info-button');
      
      if(closeMinOrderInfoBtn) {
        closeMinOrderInfoBtn.addEventListener('click', this.#handleCloseInfoButton);
      }

      if(closeShippingInfoBtn) {
        closeShippingInfoBtn.addEventListener('click', this.#handleCloseInfoButton);
      }

      if(this.refs.collectionCartButton) {
        this.refs.collectionCartButton.addEventListener('click', this.#handleCollectionCartButton);
      }

      if (this.refs.nextButton) {
        this.refs.nextButton.addEventListener('click', this.#handleNextButton);
      }

      // Apply persisted visibility now and once next frame in case inner DOM updates late
      this.#applyPersistedVisibility();
      requestAnimationFrame(() => this.#applyPersistedVisibility());

      // Observe morph/DOM changes inside the component to re-apply visibility when wrapper is replaced
      this._observer = new MutationObserver(() => {
        // Re-read attributes in case they changed with new HTML
        this.minOrderValue = parseFloat(this.getAttribute('data-minimum-order-value') || '0');
        this.freeShippingThreshold = parseFloat(this.getAttribute('data-free-shipping-threshold') || '0');
        this.cartTotal = parseFloat(this.getAttribute('data-cart-total') || '0');
        this.cartItemCount = parseFloat(this.getAttribute('data-cart-item-count') || '0');

        this.#applyPersistedVisibility();
      });
      this._observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-remaining-amount', 'data-remaining-free-shipping-amount', 'data-cart-item-count'] });
    } catch (error) {
      console.error('[COLLECTION-FOOTER] Error in connectedCallback', error);
      throw error;
    }
  }

  // Reads storage and remaining amount to decide visibility
  #applyPersistedVisibility = () => {
    try {
      const mode = sessionStorage.getItem('hideProgressBars') || '';

      const phase = this.#getProgressPhase();

      // Decide visibility based on phase and stored mode
      let shouldHide = false;
      switch (phase) {
        case 'empty':
          // remove the session storage
          sessionStorage.removeItem('hideProgressBars');
          shouldHide = true; // keep hidden when cart is empty
          break;
        case 'belowMin':
          shouldHide = mode === 'minOrderThreshold';
          break;
        case 'between':
          shouldHide = mode === 'freeShippingThreshold';
          break;
        case 'aboveFree':
          shouldHide = mode === 'all';
          break;
        default:
          shouldHide = false;
      }

      if (shouldHide) {
        this.#hideProgressBars();
      } else {
        this.#showProgressBars();
      }
    } catch (error) {
      console.error('[COLLECTION-FOOTER] Error in applyPersistedVisibility', error);
    }
  }

  // Determine what stage the cart is in relative to thresholds
  #getProgressPhase = () => {
    if (this.cartItemCount == 0) return 'empty';
    const total = Number(this.cartTotal);
    const mov = Number(this.minOrderValue);
    const fst = Number(this.freeShippingThreshold);
    if (!total || total <= 0 || !mov) return 'unknown';
    if (total < mov) return 'belowMin';
    if (fst && total < fst) return 'between';
    if (fst && total >= fst) return 'aboveFree';
    return 'unknown';
  }

  disconnectedCallback() {
    // Clean up event listener
    document.removeEventListener('flow-visual-update-requested', this.#handleFlowVisualStateUpdates);
    document.removeEventListener(DeliveryEvents.POSTAL_CODE_CHANGED, this.#handlePostalCodeChanged);

    const closeMinOrderInfoBtn = this.querySelector('.collection-close-min-order-info-button');
    const closeShippingInfoBtn = this.querySelector('.collection-close-shipping-info-button');

    if(closeMinOrderInfoBtn) {
      closeMinOrderInfoBtn.removeEventListener('click', this.#handleCloseInfoButton);
    }

    if(closeShippingInfoBtn) {
      closeShippingInfoBtn.removeEventListener('click', this.#handleCloseInfoButton);
    }

    if(this.refs.collectionCartButton) {
      this.refs.collectionCartButton.removeEventListener('click', this.#handleCollectionCartButton);
    }

    if (this.refs.nextButton) {
      this.refs.nextButton.removeEventListener('click', this.#handleNextButton);
    }

    // Disconnect mutation observer
    if (this._observer) {
      this._observer.disconnect();
      this._observer = undefined;
    }

    super.disconnectedCallback();
  }

  /**
   * Handles postal code change event to update checkout links
   * @param {CustomEvent} event - The postal code changed event
   */
  #handlePostalCodeChanged = (event) => {
    try {
      const { postalCode } = event.detail;

      // Use shared utility to update checkout links
      /** @type {any} */ (window).CheckoutLinkUpdater.updateCheckoutLinks(postalCode, this);
      
    } catch (error) {
      console.error('[COLLECTION-FOOTER] Error in handlePostalCodeChanged', error);
    }
  };

  /**
   * Handles the close button functionality
   */
  #handleCloseInfoButton = () => {
    try {
      // Persist hidden state based on current phase
      const phase = this.#getProgressPhase();
      let modeToSet = '';
      if (phase === 'belowMin') modeToSet = 'minOrderThreshold';
      else if (phase === 'between') modeToSet = 'freeShippingThreshold';
      else if (phase === 'aboveFree') modeToSet = 'all';
      if (modeToSet) {
        sessionStorage.setItem('hideProgressBars', modeToSet);
      }

      this.#hideProgressBars();
    } catch (error) {
      console.error('[COLLECTION-FOOTER] Error in handleCloseInfoButton', error);
      throw error;
    }
  }

  // Show/hide helpers keep class manipulation in one place
  #hideProgressBars = () => {
    const customFooterSection = this.closest('[id^="shopify-section-"]'); 
    if(customFooterSection) {
      customFooterSection.classList.add("hideProgressBars");
    }
  }

  #showProgressBars = () => {
    sessionStorage.removeItem('hideProgressBars');
    const customFooterSection = this.closest('[id^="shopify-section-"]'); 
    if(customFooterSection) {
      customFooterSection.classList.remove("hideProgressBars");
    }
  }

  /**
   * Handles collection cart button click
   * @param {Event} event - The click event
   */
  #handleCollectionCartButton = async (event) => {
    try {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && 'open' in cartDrawer && typeof /** @type {any} */ (cartDrawer).open === 'function') {
        /** @type {any} */ (cartDrawer).open();
      }
    } catch (error) {
      console.error('[COLLECTION-FOOTER] Error in handleCollectionCartButton', error);
      throw error;
    }
  }

  #handleCartClose = async () => {
    try {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && 'close' in cartDrawer && typeof /** @type {any} */ (cartDrawer).close === 'function') {
        /** @type {any} */ (cartDrawer).close();
      }
    } catch (error) {
      console.error('[COLLECTION-FOOTER] Error in handleCartClose', error);
      throw error;
    }
  }

  /** @param {CustomEvent} event */ 
  #handleFlowVisualStateUpdates = async (event) => {
    try {
      const stepsElements = document.querySelectorAll('.step-wrapper');
      this.stepInfo = StepInfoManager.getStepInfo();
      /** @type {number} */
      let steps = this.stepInfo ? this.stepInfo['step-count'] : 1;

      // Apply filtering only if we're not on the final step and steps are progressing
      if(this.stepsCount && steps && this.stepsCount > steps) {
        if(stepsElements.length > 0) {
          let stepType = stepsElements[steps - 1]?.getAttribute('data-step-type') || '';
          await this.#applyStepFiltering(stepType);
        }
      }
    } catch (error) {
      console.error('[COLLECTION-FOOTER] Error in handleFlowVisualStateUpdates', error);
      throw error;
    }
  }

  /**
   * Optimized step filtering: removes all filters except inventory levels, then applies product type filter
   * @param {string} stepType - The step type to filter by
   * @returns {Promise<boolean>} - True if filtering was successful
   */
  #applyStepFiltering = async (stepType) => {
    // Prefer the drawer facets form; quick filters are wired to this instance.
    const facetsForm =
      document.querySelector('#filters-drawer facets-form-component') ||
      document.querySelector('facets-form-component');
    
    if (!facetsForm || !(facetsForm instanceof HTMLElement)) {
      return false;
    }

    try {
      // Add loading spinner to body
      LoadManager.addLoader();
      
      // Get current form data and form element
      const actualForm = facetsForm.querySelector ? facetsForm.querySelector('form') : facetsForm;
      if (!actualForm || !(actualForm instanceof HTMLFormElement)) {
        return false;
      }

      const preservedParams = new URLSearchParams();
      const allowedStepTypes = new Set(['maaltijden', 'weekmenu']);
      const isAllowedStep = allowedStepTypes.has(stepType);

      // Remove "Alle" + "Aanbevolen" when not in an allowed step.
      if (!isAllowedStep) {
        const alleInput = document.querySelector('#filters-drawer input[data-label="Alle"]');
        const aanbevolenInput = document.querySelector('#filters-drawer input[data-label="Aanbevolen"]');
        if (alleInput instanceof HTMLInputElement) alleInput.checked = false;
        if (aanbevolenInput instanceof HTMLInputElement) aanbevolenInput.checked = false;
      }

      // Clear all filters except inventory/product_type/availability and (optionally) Aanbevolen for allowed steps.
      // This ensures step changes reset diet/etc filters per requirements.
      const recoParamName = 'filter.p.m.custom.product_recommendations';
      const inputsToMaybeClear = Array.from(actualForm.querySelectorAll('input[type="checkbox"]:checked'));
      inputsToMaybeClear.forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        const name = input.name || '';
        const value = input.value || '';
        const label = input.dataset?.label || '';

        const keepInventory = name.includes('inventory_levels');
        // const keepProductType = name.includes('custom.product_type');
        const keepAvailability = name.includes('availability');
        const keepAanbevolen =
          isEcommCollectionRecommendationsEnabled() &&
          isAllowedStep &&
          name === recoParamName &&
          (value === 'Aanbevolen' || label === 'Aanbevolen');

        if (!(keepInventory || keepAvailability || keepAanbevolen)) { // || keepProductType
          input.checked = false;
        }
      });

      // IMPORTANT: rebuild FormData AFTER we uncheck inputs.
      // Otherwise we might carry over diet/other filters from the pre-clear form state.
      const formData = new FormData(actualForm);

      // Step 1: Single pass: preserve inventory filters (and optionally Aanbevolen for allowed steps).
      for (const [key, value] of formData.entries()) {
        if (typeof value !== 'string') continue;

        if (key.includes('inventory_levels') || key.includes('availability')) {
          preservedParams.append(key, value);
          continue;
        }

        if (isEcommCollectionRecommendationsEnabled() && isAllowedStep && key === 'filter.p.m.custom.product_recommendations' && value === 'Aanbevolen') {
          preservedParams.append(key, value);
          continue;
        }
      }

      // Step 2: Find and prepare product type filters
      const productTypeInputs = facetsForm.querySelectorAll('input[name*="custom.product_type"]');

      if (productTypeInputs.length === 0) {
        console.warn('⚠️ [STEP-FILTER] No product type filter inputs found');
      }

      // Step 3: Find matching product type filter for stepType
      let matchingProductTypeFilter = FilterUpdateManager.findMatchingProductTypeFilter(stepType, productTypeInputs);
      
      // Step 4: Add matching product type filter to preserved parameters
      if (matchingProductTypeFilter) {
        const filter = /** @type {any} */ (matchingProductTypeFilter);
        // Toggle the matching product type filter to checked state
        const targetInput = facetsForm.querySelector(`input[name="${filter.name}"][value="${filter.value}"]`);
        if (targetInput instanceof HTMLInputElement) {
          targetInput.checked = true;
        }
        preservedParams.append(filter.name, filter.value);
      }

      // Step 5: Apply all filters in one operation
      const targetUrl = new URL(window.location.pathname, window.location.origin);
      for (const [key, value] of preservedParams.entries()) {
        if (typeof value === 'string') {
          targetUrl.searchParams.append(key, value);
        }
      }
      
      // Apply the filters using the helper function that automatically chooses the best method
      const success = await FilterUpdateManager.updateFiltersWithBestMethod(facetsForm, targetUrl.toString());
      
      if (success) {
        const cleanUrl = new URL(window.location.pathname, window.location.origin);
        const RECO_PARAM = 'filter.p.m.custom.product_recommendations';
        const aanbevolenInput = document.querySelector('#filters-drawer input[data-label="Aanbevolen"]');
        const shouldKeepAanbevolen =
          isEcommCollectionRecommendationsEnabled() &&
          isAllowedStep &&
          aanbevolenInput instanceof HTMLInputElement &&
          aanbevolenInput.checked === true;

        // Do NOT preserve any non-filter query params (like pb=0) on step changes.
        // Only keep the "Aanbevolen" param when it's active and the step type allows it.
        if (shouldKeepAanbevolen) {
          cleanUrl.searchParams.append(RECO_PARAM, 'Aanbevolen');
        }
        
        // Update the browser URL without triggering a page reload
        history.replaceState({}, '', cleanUrl.toString());

        return true;
      } else {
        return await FilterUpdateManager.applyFiltersFallback(facetsForm, matchingProductTypeFilter);
      }

    } catch (error) {
      console.error('❌ [STEP-FILTER] Error in optimized step filtering:', error);
      LoadManager.removeLoader();
      
      return false;
    } finally {
      LoadManager.removeLoader();
    }
  }

  /**
   * Handles cart next button click
   * @param {Event} event - The click event
   */
  handleCartNextButton = async (event) => {
    await this.#handleNextButton(event);
  }

  /**
   * Handles next button click
   * @param {Event} event - The click event
   */
  #handleNextButton = async (event) => {
    try {
      const stepsElements = document.querySelectorAll('.step-wrapper');
      this.stepInfo = StepInfoManager.getStepInfo();
      /** @type {number} */
      let steps = this.stepInfo ? this.stepInfo['step-count'] : 1;

      // Validate that we have valid step info before proceeding
      if (!this.stepInfo) {
        return;
      }

      if(this.stepsCount && steps && this.stepsCount >= steps + 1) {

        if(this.stepsCount > (steps + 1)) {
          steps = steps + 1;
          this.stepInfo = StepInfoManager.updateStepInfo({ 'step-count': steps });
          StepInfoManager.dispatchFlowStateChanged(this.stepInfo);

          if(stepsElements.length > 0) {
            let stepType = stepsElements[steps - 1]?.getAttribute('data-step-type') || '';
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            await this.#applyStepFiltering(stepType);
            await this.#handleCartClose();
          }
        } else {
          // Final step - proceed to checkout
          const checkoutUrl = this.refs.nextButton?.getAttribute('data-href') || '';
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            // setTimeout(() => { window.location.href = checkoutUrl; }, 1000);
          }
        }
      }
    } catch (error) {
      console.error('[COLLECTION-FOOTER] Error in handleNextButton', error);
      throw error;
    }
  }

}

if (!customElements.get('custom-collection-footer-component')) {
  customElements.define('custom-collection-footer-component', CustomCollectionFooterComponent);
}
